// cSpell:ignore afrekening betaaldetails dezelfde geopend Herbruikt opgeslagen zelfde
// Sidebar-detail van een opgeslagen afrekening (zelfde patroon als de
// betaaldetails): geopend via het `settlementId` query param. Herbruikt de
// ConfirmedView uit de wizard, met dezelfde data-wiring als SettlementForm.

import * as propertyHooks from "@rentiohq/shared-frontend/dist/reduxV2/property/property.hooks";
import * as settlementPromises from "@rentiohq/shared-frontend/dist/reduxV2/settlement/settlement.promises";
import { confirm } from "@rentiohq/shared-frontend/dist/utils/confirm.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import React from "react";
import { useDispatch } from "react-redux";
import { useMatch } from "react-router-dom";
import { MissingDetail } from "scenes/Settlements/components/MissingDetail";
import { isSettlementViewReadOnly } from "scenes/Settlements/costsEditAccess.utils";
import { settlementDeleteBlockedMessage } from "scenes/Settlements/costsList.utils";
import {
  useAssignableProperties,
  useCanEditCosts,
  useGroupRentalContracts,
  useGroupUnits,
  useInvoices,
  useKeys,
  usePropertyRentalContracts,
  deleteRefusedMessage,
  useSettlement,
  useSettlementPaymentStatus,
} from "scenes/Settlements/hooks";
import { getMissingDetailState } from "scenes/Settlements/missingDetail.utils";
import { ConfirmedView } from "scenes/Settlements/scenes/SettlementInfo/SettlementForm/steps/ConfirmedView";
import { paymentRequestPropertyIds } from "scenes/Settlements/settlementPaymentStatus.utils";
import { departureSettlementCosts } from "scenes/Settlements/settlementView.utils";
import { isDepartureSettlement } from "scenes/Settlements/types";
import { useWithArchivedProperties } from "scenes/Settlements/useArchivedProperties";

interface IProps {
  settlementId: string;
  onDrawerClose?: () => void;
}

const Settlement: React.FC<IProps> = ({ settlementId, onDrawerClose }) => {
  const dispatch = useDispatch();
  const {
    settlement,
    isFetching: isFetchingSettlement,
    fetchError: settlementFetchError,
    recordActions,
    refetch: refetchSettlement,
    reload: reloadSettlement,
  } = useSettlement(settlementId);
  // An owner who sees only their own part of a group settlement only reads it.
  const canEdit =
    useCanEditCosts(settlement?.groupId ?? settlement?.unitId) &&
    !settlement?.snapshot?.viewerPart;

  const { items: activeProperties, isFetching: isFetchingProperties } =
    useAssignableProperties();
  // The property list leaves archived properties out: the settled one is
  // added when archived, so a unit keeps its group (which provision slice it
  // settled) and its address. Unmarked, as the statements built here name it.
  const settledPropertyId = settlement?.unitId ?? settlement?.groupId;
  const assignable = useWithArchivedProperties({
    properties: activeProperties,
    isFetching: isFetchingProperties,
    ids: [settledPropertyId],
    marked: false,
  });
  // A departure settlement also settled its share of the parent group's
  // costs, split by the group's keys (see ISettledDeparture).
  const departureGroupId =
    settlement?.scope === "unit" && isDepartureSettlement(settlement)
      ? assignable.find(p => p.id === settlement.unitId)?.parentId
      : undefined;

  const { units: groupUnits } = useGroupUnits(
    settlement?.scope === "group" ? settlement.groupId : undefined,
  );
  const unitIds =
    settlement?.scope === "group"
      ? groupUnits.map(u => String(u.id))
      : undefined;
  const filter =
    settlement?.scope === "group"
      ? { groupId: settlement.groupId, unitIds }
      : { unitId: settlement?.unitId, parentGroupId: departureGroupId };
  const {
    items: fetchedInvoices,
    isLoading: isLoadingInvoices,
    loadFailed: invoicesLoadFailed,
    refetch: refetchInvoices,
  } = useInvoices(filter);
  const invoices = React.useMemo(
    () => departureSettlementCosts(fetchedInvoices, departureGroupId),
    [fetchedInvoices, departureGroupId],
  );

  const unitRentals = usePropertyRentalContracts(
    settlement?.scope === "unit" && settlement.unitId
      ? Number(settlement.unitId)
      : undefined,
  );
  const groupRentals = useGroupRentalContracts(
    settlement?.scope === "group" ? groupUnits.map(u => u.id) : [],
  );
  const contracts = React.useMemo(
    () =>
      (settlement?.scope === "group" ? groupRentals : unitRentals).map(
        r => r.ref,
      ),
    [settlement?.scope, groupRentals, unitRentals],
  );
  const { items: keys } = useKeys(
    settlement?.groupId ?? departureGroupId ?? "",
  );
  const { detail: settledProperty } = propertyHooks.useDetail({
    id: settledPropertyId ? Number(settledPropertyId) : undefined,
    shouldRefetch: !!settledPropertyId,
  });

  // A group settlement is managed on the group, a unit's on the unit. Opened
  // on another property's page (a unit's cost tab or payments, or the
  // building's list of its units' settlements) it is read-only: no delete
  // and no tenant actions there. The settlement of an archived property is only
  // read until the property is activated again, like its costs.
  const propertyPage = useMatch("/properties/:propertyId/*");
  const isReadOnly = isSettlementViewReadOnly({
    isPropertyArchived: !!settledProperty?.archivedAt,
    scope: settlement?.scope,
    groupId: settlement?.groupId,
    unitId: settlement?.unitId,
    pagePropertyId: propertyPage?.params.propertyId,
  });

  // The live payment status of its tenants, for the delete check.
  const paymentStatusSettlements = React.useMemo(
    () => (settlement ? [settlement] : []),
    [settlement],
  );
  const paymentStatusMaps = useSettlementPaymentStatus(
    paymentStatusSettlements,
    React.useMemo(
      () =>
        paymentRequestPropertyIds({
          propertyIds: [],
          settlements: paymentStatusSettlements,
        }),
      [paymentStatusSettlements],
    ),
  );

  // A link to a settlement that is gone or not the viewer's says so instead
  // of loading forever (X05).
  if (!settlement) {
    return (
      <MissingDetail
        kind="settlement"
        state={getMissingDetailState({
          isFetching: isFetchingSettlement,
          fetchError: settlementFetchError,
        })}
        onClose={onDrawerClose}
        onRetry={refetchSettlement}
      />
    );
  }

  const handleDelete = () => {
    // A tenant payment still open, or a payment made, stops the delete before
    // the broker is asked to confirm, as in the costs tab (F07): the confirm
    // says the costs "can be settled again", which a paid settlement never
    // allows.
    const blocked = settlementDeleteBlockedMessage({
      settlement,
      statusMaps: paymentStatusMaps,
    });
    if (blocked) {
      confirm({
        type: "warning",
        title: getLocalizedText("settlement.delete.title"),
        info: blocked,
        secondaryAction: {
          title: getLocalizedText("payment_request.button.ok"),
        },
      });
      return;
    }
    confirm({
      title: getLocalizedText("settlement.delete.title"),
      info: getLocalizedText("settlement.delete.confirm"),
      primaryActions: [
        {
          title: getLocalizedText("costs.action.delete"),
          onPress: async () => {
            try {
              // The refusal is explained below; no generic toast next to it.
              await settlementPromises.remove(dispatch, {
                id: settlementId,
                suppressFailureAlert: true,
              });
              onDrawerClose?.();
            } catch (error) {
              // The server refuses while payments of this settlement are
              // still open — say why instead of failing silently.
              setTimeout(() =>
                confirm({
                  type: "warning",
                  title: getLocalizedText("settlement.delete.title"),
                  info: deleteRefusedMessage(error, "settlement", {
                    isGroup: settlement.scope === "group",
                  }),
                  secondaryAction: {
                    title: getLocalizedText("payment_request.button.ok"),
                  },
                }),
              );
            }
          },
        },
      ],
    });
  };

  return (
    <ConfirmedView
      settlement={settlement}
      invoices={invoices}
      isLoadingInvoices={isLoadingInvoices}
      invoicesLoadFailed={invoicesLoadFailed}
      onRetryInvoices={refetchInvoices}
      contracts={contracts}
      keys={keys}
      assignable={assignable}
      // The view explains a refused write in a dialog of its own, and reads
      // the settlement again either way, so it shows what is stored.
      persistActions={async actions => {
        try {
          await recordActions(actions);
        } finally {
          refetchSettlement();
        }
      }}
      reloadSettlement={reloadSettlement}
      onDelete={canEdit && !isReadOnly ? handleDelete : undefined}
      readOnly={isReadOnly}
    />
  );
};

// eslint-disable-next-line import/no-default-export
export default Settlement;
